/**
 * UserSignaturePage — Pagina per caricare la propria firma e il proprio timbro.
 * Usata da Consulenti HACCP, Laboratori Analisi e altri utenti che firmano documenti.
 */
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, Trash2, CheckCircle2, FileImage, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// v1.3.22: le immagini firma/timbro sono servite da endpoint protetti (richiedono token),
// quindi un <img src> semplice riceve 401 e la miniatura non si vede. Le carichiamo via blob autenticato.
function AuthImg({ url, tsKey, ...props }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objUrl = null;
    if (!url) { setSrc(null); return; }
    api.get(url, { responseType: 'blob' })
      .then((r) => { objUrl = URL.createObjectURL(r.data); setSrc(objUrl); })
      .catch(() => setSrc(null));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [url, tsKey]);
  if (!src) return null;
  return <img src={src} {...props} />;
}

export default function UserSignaturePage() {
  const [status, setStatus] = useState({ has_firma: false, has_timbro: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const firmaRef = useRef(null);
  const timbroRef = useRef(null);
  const [tsKey, setTsKey] = useState(Date.now()); // cache buster

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users/me/signature-status');
      setStatus(data);
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post(`/users/me/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${kind === 'firma' ? 'Firma' : 'Timbro'} caricata`);
      setTsKey(Date.now());
      load();
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const remove = async (kind) => {
    if (!window.confirm(`Eliminare ${kind === 'firma' ? 'la firma' : 'il timbro'}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/me/${kind}`);
      toast.success('Eliminato');
      load();
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  const Card1 = ({ kind, label, url, has, refEl }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {label}
          {has && <span className="inline-flex items-center gap-1 text-green-700 text-xs"><CheckCircle2 className="w-3 h-3" /> caricata</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50/60 min-h-[140px] flex items-center justify-center">
          {has ? (
            <AuthImg url={url} tsKey={tsKey} alt={label} className="max-h-32 object-contain" data-testid={`${kind}-preview`} />
          ) : (
            <div className="text-slate-400 text-center">
              <FileImage className="w-10 h-10 mx-auto mb-2" />
              <p className="text-xs">Nessuna {label.toLowerCase()} caricata</p>
            </div>
          )}
        </div>
        <input
          ref={refEl}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => upload(kind, e.target.files?.[0])}
          data-testid={`${kind}-input`}
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => refEl.current?.click()} disabled={busy} data-testid={`upload-${kind}-btn`}>
            <Upload className="w-3 h-3 mr-1" /> {has ? 'Sostituisci' : 'Carica'}
          </Button>
          {has && (
            <Button size="sm" variant="outline" onClick={() => remove(kind)} disabled={busy} className="text-red-600">
              <Trash2 className="w-3 h-3 mr-1" /> Rimuovi
            </Button>
          )}
        </div>
        <p className="text-[11px] text-slate-500">PNG/JPG/WebP, max 5 MB. Per migliori risultati usa immagini su sfondo trasparente o bianco.</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 p-4 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold" data-testid="user-sig-title">Firma & Timbro</h1>
        <p className="text-sm text-slate-600">Carica la tua firma autografa e il tuo timbro professionale. Saranno inseriti automaticamente nei documenti che generi (verbali di prelievo, accettazione campioni, ecc.).</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card1 kind="firma" label="Firma" url={status.firma_url} has={status.has_firma} refEl={firmaRef} />
        <Card1 kind="timbro" label="Timbro" url={status.timbro_url} has={status.has_timbro} refEl={timbroRef} />
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4 text-xs text-slate-700 space-y-2">
          <p className="font-semibold">Dove vengono utilizzate?</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><strong>Consulente HACCP</strong>: nei <em>Verbali di Prelievo</em> generati in Word — firma e timbro appaiono in fondo nella sezione "Firma del Consulente".</li>
            <li><strong>Laboratorio Analisi</strong>: nei moduli <em>Mod.1 Accettazione Campioni</em> generati dal laboratorio quando riceve i campioni.</li>
            <li><strong>Altri utenti</strong>: in tutti i documenti che richiedono firma autografa.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
